import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import Scrollbars from "react-custom-scrollbars-2";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getAllPatients, getPatientPrescription } from ".././PharmacySales/SalesService";

import "./NewSales.scss";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { useCurrency } from "../../_helper/CurrencyContext";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";

import CustomerSelector from "./ReturnSales/components/CustomerSelector";
import CartTable from "./ReturnSales/components/CartTable";
import CartSummary from "./ReturnSales/components/CartSummary";
import PaymentModal from "./ReturnSales/components/PaymentModal";
import InvoiceModal from "./ReturnSales/components/InvoiceModal";

import { useReturnCart } from "./ReturnSales/hooks/useReturnCart";
import { useReturnPaymentManager } from "./ReturnSales/hooks/useReturnPaymentManager";
import { mapPatientsToOptions, buildPatientOptionLabel } from "./ReturnSales/utils/patientUtils";

import PrescriptionSummary from "./ReturnSales/components/PrescriptionSummary";
import { calculateItemTax } from "./NewSales/utils/taxCalculations";

const ReturnSales = () => {
    const location = useLocation();
    const { patientId, patientName, stateCustomerType } = location.state || {};

    const { t } = useTranslation();
    const { setLoading } = useLoader();
    const { pharmacyPOS } = usePharmacy();
    const { getCurrencySymbol, currency, convertCurrency } = useCurrency();
    const disableScrollProps = useDisableScrollOnFocus();

    // ✅ FIXED: Use actual hospital connection status
    const isHospitalConnected = Boolean(pharmacyPOS?.hospital_id);
    const patientSelectRef = useRef(null);
    const saleNumberInputRef = useRef(null);

    const [contentHeight, setContentHeight] = useState(window.innerHeight - 166);
    const [discountType, setDiscountType] = useState("Percentage");
    const [discountValue, setDiscountValue] = useState(0);
    const [transportCost] = useState(0);
    const [customerType, setCustomerType] = useState("walking");
    const [walkingCustomerName, setWalkingCustomerName] = useState("");
    const [saleNumberValue, setSaleNumberValue] = useState("");

    const [selectedPatient, setSelectedPatient] = useState(null);
    const [customerError, setCustomerError] = useState(false);
    const [exchangeRate] = useState(0);
    const [recentPurchases, setRecentPurchases] = useState([]);
    const [parentSalesId, setParentSalesId] = useState(null);
    const [saleSearchMode, setSaleSearchMode] = useState(false);

    // Tax system states
    const [pharmacyStateCode] = useState(pharmacyPOS?.stateCode || "");
    const [patientStateCode, setPatientStateCode] = useState("");
    const [posTaxType] = useState(pharmacyPOS?.taxType || "gst");
    const [itemTaxes, setItemTaxes] = useState({});

    const {
        cart,
        addProductToCart,
        handleBatchChange,
        updateItemField,
        handleQuantityChange,
        removeItem,
        resetCart,
        setCart
    } = useReturnCart(t);

    // Calculate item taxes whenever cart or state codes change
    useEffect(() => {
        if (cart.length > 0) {
            const newItemTaxes = {};
            cart.forEach(item => {
                const taxInfo = calculateItemTax(
                    item,
                    pharmacyStateCode,
                    patientStateCode,
                    posTaxType
                );
                newItemTaxes[`${item._id}-${item.selectedBatchId}`] = taxInfo;
            });
            setItemTaxes(newItemTaxes);
        } else {
            setItemTaxes({});
        }
    }, [cart, pharmacyStateCode, patientStateCode, posTaxType]);

    const {
        paymentData,
        setPaymentData,
        showPaymentModal,
        openPaymentModal,
        closePaymentModal,
        handlePaymentSave,
        errors,
        showInvoice,
        invoiceData,
        closeInvoice,
        resetPaymentState,
        totals,
    } = useReturnPaymentManager({
        t,
        cart,
        itemTaxes,
        discountType,
        discountValue,
        transportCost,
        customerType,
        selectedPatient,
        exchangeRate,
        pharmacyPOS,
        getCurrencySymbol,
        setLoading,
        parentSalesId,
        convertCurrency,
        currency,
        posTaxType,
        pharmacyStateCode,
        patientStateCode
    });

    const [recentPrescriptions, setRecentPrescriptions] = useState([]);
    const [showPrescriptionsCard, setShowPrescriptionsCard] = useState(false);

    // ✅ Load patients with clinic_id only for hospital_patient type
    const loadPatients = useCallback(
        async (inputValue) => {
            if (!inputValue || inputValue.length < 3) return [];

            try {
                const payload = {
                    search: inputValue,
                };

                // Only add clinic_id if hospital connected AND customer type is hospital_patient
                if (isHospitalConnected && customerType === "hospital_patient") {
                    payload.clinic_id = pharmacyPOS.hospital_id;
                    console.log("🏥 Loading hospital patients with clinic_id:", pharmacyPOS.hospital_id);
                } else {
                    console.log("🏪 Loading pharmacy patients without clinic_id");
                }

                const response = await getAllPatients(payload);
                const patients = response.data?.data || [];

                return mapPatientsToOptions(patients, isHospitalConnected, customerType);
            } catch (error) {
                console.error("Error searching patients:", error);
                return [];
            }
        },
        [isHospitalConnected, customerType, pharmacyPOS?.hospital_id]
    );

    // ✅ Select a sale/return item
    const handleSelectSale = useCallback(
        (sale) => {
            setParentSalesId(sale._id);
            resetCart();
            // console.log(sale);

            sale.salesHistory.forEach((item) => {
                addProductToCart({
                    _id: item.itemId,
                    stockId: item.stockId,
                    batchNumber: item.batchNumber,
                    brandName: item.brandName || item.medicineName,
                    isOTC: item.isOTC,
                    saltName: item.medicineName || item.saltName || "",
                    manufacturer: item.manufacturer || "",
                    quantity: item.medicineQunatity || 0,
                    salePrice: item.unitPricePerMedicine || 0,
                    selectedBatchId: item.stockId,
                    batchList: [{ _id: item.stockId, batchNumber: item.batchNumber }],
                    isReturn: item.isReturn,
                    // Preserve original tax information
                    taxPercentage: parseFloat(item.taxPercentage) || 0,
                    CGSTPercentage: parseFloat(item.CGSTPercentage) || 0,
                    SGSTPercentage: parseFloat(item.SGSTPercentage) || 0,
                    IGSTPercentage: parseFloat(item.IGSTPercentage) || 0,
                    isIGST: item.isIGST || false,
                    CGST: parseFloat(item.CGST) || 0,
                    SGST: parseFloat(item.SGST) || 0,
                    IGST: parseFloat(item.IGST) || 0,
                    tax: parseFloat(item.tax) || 0
                });
            });
            // set discount value from sale
            setDiscountValue(sale.discount);
            setDiscountType(sale.discountType === "percentage" ? "Percentage" : "Fixed");

            // console.log("✅ Sale selected and items added to cart:", sale._id);
        },
        [addProductToCart, resetCart]
    );

    // ✅ Search by sale number with clinic_id support
    const handleSearchBySaleNumber = useCallback(
        async (saleNumber) => {
            if (!saleNumber || saleNumber.trim() === "") {
                toast.info(t("Please enter a sale/invoice number"));
                return;
            }

            try {
                setLoading(true);
                const payload = {
                    saleNumber: saleNumber.trim(),
                };

                // Only add clinic_id if hospital connected AND customer type is hospital_patient
                if (isHospitalConnected && customerType === "hospital_patient") {
                    payload.clinic_id = pharmacyPOS.hospital_id;
                    console.log("🏥 Searching sale with clinic_id:", pharmacyPOS.hospital_id);
                } else {
                    console.log("🏪 Searching sale without clinic_id");
                }

                const response = await getPatientPrescription(payload);

                if (response.data.status === 'success') {
                    const sales = response.data.data.lastFewSales || [];

                    if (sales.length === 0) {
                        toast.warning(t("Sale not found"));
                        setRecentPurchases([]);
                        return;
                    }

                    // Get the first (most recent) sale
                    const sale = sales[0];

                    // Set walking customer name from sale if available
                    if (sale.patientName) {
                        setWalkingCustomerName(sale.patientName);
                        setPaymentData(prev => ({ ...prev, patientName: sale.patientName }));
                    }

                    // Set customer type based on the sale's customer type
                    setCustomerType(sale.customerType || "walking");

                    // Set recent purchases to show all matching sales
                    setRecentPurchases(sales);
                    setShowPrescriptionsCard(true);

                    console.log("✅ Sale found:", sale._id);
                    // toast.success(t("Sale found successfully"));
                } else {
                    toast.warning(t("Sale not found"));
                    setRecentPurchases([]);
                }
            } catch (error) {
                console.error("Error searching sale:", error);
                toast.warning(t("Error searching sale. Please check the number and try again."));
                setRecentPurchases([]);
            } finally {
                setLoading(false);
            }
        },
        [isHospitalConnected, customerType, pharmacyPOS?.hospital_id, setLoading, t, setPaymentData, setShowPrescriptionsCard]
    );

    // ✅ Load recent purchases with clinic_id support
    const loadRecentPurchases = useCallback(
        async (patientId) => {
            try {
                setLoading(true);
                const body = {
                    patient_id: patientId,
                    pageNo: 1,
                    pageSize: 10,
                };

                // Only add clinic_id if hospital connected AND customer type is hospital_patient
                // if (isHospitalConnected && customerType === "hospital_patient") {
                //     body.clinic_id = pharmacyPOS.hospital_id;
                //     body.isHospitalPatient = true;
                //     console.log("🏥 Loading hospital patient purchases with clinic_id:", pharmacyPOS.hospital_id);
                // } else {
                //     console.log("🏪 Loading pharmacy patient purchases without clinic_id");
                // }

                const response = await getPatientPrescription(body);
                const sales = response.data?.data?.lastFewSales || [];
                setRecentPurchases(sales);
                // console.log("✅ Recent purchases ", sales);
            } catch (err) {
                console.error("Error loading sales:", err);
                setRecentPurchases([]);
            } finally {
                setLoading(false);
            }
        },
        [isHospitalConnected, customerType, pharmacyPOS?.hospital_id, setLoading]
    );

    const handleClosePrescriptionCard = useCallback(() => {
        setShowPrescriptionsCard(false);
        setRecentPurchases([]);
    }, []);

    // ✅ Handle patient selection with clinic_id support
    const handlePatientChange = useCallback(
        async (selectedOption) => {
            const raw = selectedOption ? selectedOption.data : null;
            // Normalize patient object with all name fields
            const patient = raw
                ? {
                    ...raw,
                    full_name: raw.full_name || raw.patient_name || raw.name || "",
                    patient_name: raw.patient_name || raw.full_name || raw.name || "",
                    name: raw.name || raw.full_name || raw.patient_name || "",
                    stateCode: raw.stateCode || ""
                }
                : null;

            setSelectedPatient(patient);
            setPatientStateCode(patient?.stateCode || "");
            setCustomerError(false);
            setSaleSearchMode(false);

            if (patient) {
                const displayName = buildPatientOptionLabel(patient, isHospitalConnected);
                setPaymentData((prev) => ({ ...prev, patientName: displayName }));
                await loadRecentPurchases(patient._id);
                setShowPrescriptionsCard(true);
                console.log("✅ Patient selected:", patient._id);
            } else {
                setPaymentData((prev) => ({ ...prev, patientName: "" }));
                setRecentPurchases([]);
                setShowPrescriptionsCard(false);
            }
        },
        [isHospitalConnected, loadRecentPurchases, setPaymentData]
    );

    // ✅ Handle customer type change with proper cleanup
    const handleCustomerTypeChange = useCallback(
        (type) => {
            if (type === customerType) return;

            console.log("🔄 Customer type changing to:", type);
            setCustomerType(type);
            setCustomerError(false);
            setSaleNumberValue("");
            resetCart();
            setRecentPurchases([]);
            setShowPrescriptionsCard(false);

            if (type === "walking") {
                // Switching to walk-in
                setSelectedPatient(null);
                setPatientStateCode("");
                setPaymentData((prev) => ({ ...prev, patientName: walkingCustomerName }));
                if (patientSelectRef.current) {
                    patientSelectRef.current.setValue(null);
                }
            } else {
                // Switching to existing customer or hospital patient
                setWalkingCustomerName("");
                setSelectedPatient(null);
                setPatientStateCode("");
                setPaymentData((prev) => ({ ...prev, patientName: "" }));
                if (patientSelectRef.current) {
                    patientSelectRef.current.setValue(null);
                }
            }
        },
        [customerType, walkingCustomerName, resetCart, setPaymentData]
    );

    const handleWalkingNameChange = useCallback(
        (value) => {
            setWalkingCustomerName(value);
            setPaymentData((prev) => ({ ...prev, patientName: value }));
            setCustomerError(false);
            setSaleSearchMode(false);
        },
        [setPaymentData]
    );

    const handleOpenPaymentModal = useCallback(() => {
        setCustomerError(false);

        if (cart.length === 0) {
            toast.info(t("Please select at least one product before saving."));
            return;
        }

        const netAmount = totals.netTotal;

        if (customerType === "inpatient" || customerType === "hospital_patient") {
            if (!selectedPatient) {
                setCustomerError(true);
                Swal.fire({
                    icon: "info",
                    text: t("Please select a patient first"),
                    confirmButtonText: t("OK"),
                });
                return;
            }
            const name = buildPatientOptionLabel(selectedPatient, isHospitalConnected);
            openPaymentModal({ customerName: name, amount: netAmount });
        } else {
            if (paymentData.patientName && paymentData.patientName.trim() !== "") {
                openPaymentModal({ customerName: paymentData.patientName, amount: netAmount });
            } else {
                setCustomerError(true);
                Swal.fire({
                    icon: "info",
                    text: t("Please enter walk-in customer name or search by sale number"),
                    confirmButtonText: t("OK"),
                });
            }
        }
    }, [
        cart.length,
        customerType,
        openPaymentModal,
        paymentData.patientName,
        selectedPatient,
        t,
        totals.netTotal,
        isHospitalConnected
    ]);

    const resetSale = useCallback(() => {
        resetCart();
        resetPaymentState();
        setDiscountType("Percentage");
        setDiscountValue(0);
        setCustomerType("walking");
        setWalkingCustomerName("");
        setSelectedPatient(null);
        setPatientStateCode("");
        setCustomerError(false);
        handleClosePrescriptionCard();
        setRecentPrescriptions([]);
        setShowPrescriptionsCard(false);
        setItemTaxes({});
        setSaleSearchMode(false);
        setSaleNumberValue("");
        if (patientSelectRef.current) {
            patientSelectRef.current.setValue(null);
        }
    }, [
        resetCart,
        resetPaymentState,
        handleClosePrescriptionCard,
    ]);

    const printInvoice = useCallback(() => {
        const invoiceContent = document?.getElementById("invoice-content");
        if (!invoiceContent) return;
        const printWindow = window.open("", "", "width=400,height=600");
        if (!printWindow) return;
        printWindow.document.write(`
        <html>
            <head>
            <title>Invoice</title>
            <style>
                @page { size: 80mm auto; margin: 5mm; }
                body { font-family: Arial, sans-serif; font-size: 12px; width: 80mm; margin: 0 auto; }
                .text-center { text-align: center; }
                .text-end { text-align: right; }
                .bold { font-weight: bold; }
                .dotted { border-bottom: 1px dotted #000; margin: 4px 0; }
                table { width: 100%; border-collapse: collapse; font-size: 12px; }
                td { padding: 2px 0; vertical-align: top; }
                .section { margin-top: 6px; margin-bottom: 6px; }
                .thankyou { margin-top: 12px; font-size: 12px; text-align: center; }
            </style>
            </head>
            <body>${invoiceContent.innerHTML}</body>
        </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 400);
    }, []);

    useEffect(() => {
        const handleResize = () => setContentHeight(window.innerHeight - 166);
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (patientId && patientName) {
            const patient = {
                _id: patientId,
                full_name: patientName,
                patient_name: patientName,
                name: patientName,
                stateCode: ""
            };
            setSelectedPatient(patient);
            setCustomerType(stateCustomerType);
            setPaymentData((prev) => ({ ...prev, patientName }));
            if (patientSelectRef.current) {
                patientSelectRef.current.setValue({
                    value: patient._id,
                    label: patient.full_name,
                    data: patient,
                });
            }
            loadRecentPurchases(patientId);
        }
    }, [patientId, patientName, setPaymentData, loadRecentPurchases]);

    const handleSubmitReturn = useCallback(async () => {
        const success = await handlePaymentSave();
        if (success) {
            // Hook handles invoice modal
        }
    }, [handlePaymentSave]);

    const updateReturnQuantity = useCallback(
        (_id, value, maxValue) => {
            const val = Math.max(0, Math.min(Number(value), maxValue));

            setCart(prev =>
                prev.map(item =>
                    item._id === _id
                        ? { ...item, returnQuantity: val }
                        : item
                )
            );
        },
        [setCart]
    );

    return (
        <Fragment>
            <div className="new-sales return-sales-theme" style={{ minHeight: `${contentHeight}px` }}>
                <div className="content-wrapper" style={{ height: "100%" }}>
                    <Row className="pt-1 g-3 pos-content" style={{ minHeight: `${contentHeight}px` }}>
                        <Col sm={12} md={8}>
                            <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                                <Card>
                                    <Card.Body className="p-2">
                                        <CustomerSelector
                                            t={t}
                                            isHospitalConnected={isHospitalConnected}
                                            customerType={customerType}
                                            onCustomerTypeChange={handleCustomerTypeChange}
                                            walkingCustomerName={walkingCustomerName}
                                            onWalkingNameChange={handleWalkingNameChange}
                                            customerError={customerError}
                                            patientSelectRef={patientSelectRef}
                                            loadPatients={loadPatients}
                                            onPatientChange={handlePatientChange}
                                            disableScrollProps={disableScrollProps}
                                            onSearchBySaleNumber={handleSearchBySaleNumber}
                                            saleSearchMode={saleSearchMode}
                                            onSaleSearchModeChange={setSaleSearchMode}
                                            saleNumberInputRef={saleNumberInputRef}
                                            saleNumberValue={saleNumberValue}
                                            setSaleNumberValue={setSaleNumberValue}
                                        />
                                        <Scrollbars
                                            autoHide
                                            style={{ height: `${contentHeight - 250}px` }}
                                            universal
                                            renderTrackHorizontal={(props) => <div {...props} style={{ display: "none" }} />}
                                        >
                                            <CartTable
                                                t={t}
                                                cart={cart}
                                                getCurrencySymbol={getCurrencySymbol}
                                                onBatchChange={handleBatchChange}
                                                onPriceChange={updateItemField}
                                                onQuantityChange={handleQuantityChange}
                                                onRemoveItem={removeItem}
                                                disableScrollProps={disableScrollProps}
                                                updateReturnQuantity={updateReturnQuantity}
                                                itemTaxes={itemTaxes}
                                                posTaxType={posTaxType}
                                                pharmacyStateCode={pharmacyStateCode}
												patientStateCode={patientStateCode}
												totals={totals}
                                            />
                                        </Scrollbars>
                                    </Card.Body>
                                </Card>
                                <Card>
                                    <Card.Body className="p-2">
                                        <Scrollbars
                                            autoHide
                                            style={{ height: `${contentHeight - 250}px` }}
                                            universal
                                            renderTrackHorizontal={(props) => <div {...props} style={{ display: "none" }} />}
                                        >
                                            <CartSummary
                                                t={t}
                                                discountType={discountType}
                                                onDiscountTypeChange={setDiscountType}
                                                discountValue={discountValue}
                                                onDiscountValueChange={(value) => setDiscountValue(Number(value) || 0)}
                                                totals={totals}
                                                getCurrencySymbol={getCurrencySymbol}
                                                disableScrollProps={disableScrollProps}
                                                posTaxType={posTaxType}
                                                pharmacyStateCode={pharmacyStateCode}
                                                patientStateCode={patientStateCode}
                                                cart={cart}
                                                itemTaxes={itemTaxes}
                                            />
                                        </Scrollbars>
                                        <div className="row w-100 justify-content-end mx-auto mt-3 py-3 border-top">
                                            <div className="col-lg-5">
                                                <Button
                                                    variant="danger"
                                                    size="md"
                                                    className="me-2 w-100"
                                                    style={{ fontWeight: "bold" }}
                                                    onClick={handleOpenPaymentModal}
                                                >
                                                    {t("Process Return")}
                                                </Button>
                                            </div>
                                            <div className="col-lg-2">
                                                <Button variant="secondary" size="md" className="w-100" onClick={resetSale}>
                                                    {t("Reset")}
                                                </Button>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        </Col>
                        <Col sm={12} md={4} style={{ position: "relative" }}>
                            <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                                {showPrescriptionsCard && (
                                    <PrescriptionSummary
                                        t={t}
                                        selectedPatient={selectedPatient}
                                        recentPrescriptions={recentPurchases}
                                        onClose={handleClosePrescriptionCard}
                                        onSelectPrescription={handleSelectSale}
                                        isHospitalConnected={isHospitalConnected}
                                        isWalkingCustomer={customerType === "walking" && saleSearchMode}
                                        walkingCustomerName={walkingCustomerName}
                                    />
                                )}
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
            <PaymentModal
                t={t}
                show={showPaymentModal}
                onHide={closePaymentModal}
                paymentData={paymentData}
                onPaymentDataChange={(patch) => setPaymentData((prev) => ({ ...prev, ...patch }))}
                errors={errors}
                handleSave={handleSubmitReturn}
                disableScrollProps={disableScrollProps}
                totals={totals}
                getCurrencySymbol={getCurrencySymbol}
                convertCurrency={convertCurrency}
                defaultCurrency={currency}
            />
            <InvoiceModal
                t={t}
                show={showInvoice}
                invoiceData={invoiceData}
                onHide={closeInvoice}
                onClose={() => {
                    closeInvoice();
                    resetSale();
                }}
                onPrint={printInvoice}
                pharmacyData={pharmacyPOS}
            />
        </Fragment>
    );
};

export default ReturnSales;